import React, { memo, useRef } from 'react';
import { BalloonEventModel } from 'ts-core';
import { EventItem } from './EventItem';
import { LazyVisible } from './LazyVisible';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useBalloonEventsData } from '../contexts/EventData';

// EventItem renders at `h-37` (148px); reserve that for the placeholder so
// scroll position doesn't shift when items mount in.
const EVENT_ITEM_PLACEHOLDER_HEIGHT = 148;

export interface EventItemsListProps {
    events: BalloonEventModel[];
    onLoadMore: () => void;
    hasMore: boolean;
    isLoading?: boolean;
}

export const EventItemsList: React.FC<EventItemsListProps> = memo(function EventItemsList({
    events,
    onLoadMore,
    hasMore,
    isLoading = false,
}) {
    const { selectedEvent } = useBalloonEventsData();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useInfiniteScroll({
        onLoadMore,
        hasMore,
        isLoading,
        threshold: 200,
    });

    if (events.length === 0 && !isLoading) {
        return (
            <div className="events-items-list w-full h-full flex items-center justify-center">
                <p className="text-Text text-center">No events found</p>
            </div>
        );
    }

    return (
        <div ref={scrollContainerRef} className="events-items-list w-full h-full overflow-y-auto overflow-x-hidden">
            <div className="px-[18px] py-4 flex flex-col gap-4">
                {events.map((event) => (
                    <LazyVisible
                        key={event.id}
                        rootRef={scrollContainerRef}
                        placeholderHeight={EVENT_ITEM_PLACEHOLDER_HEIGHT}
                    >
                        <EventItem
                            event={event}
                            selected={selectedEvent?.id === event.id}
                            hasSelectedEvent={!!selectedEvent}
                        />
                    </LazyVisible>
                ))}
                
                {/* Infinite Scroll Sentinel */}
                {hasMore && (
                    <div ref={sentinelRef} className="w-full h-1" />
                )}
                
                {/* Loading Indicator */}
                {isLoading && (
                    <div className="w-full flex justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-Balloon"></div>
                    </div>
                )}
            </div>
        </div>
    );
});
